/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import MenuListHeader from './menu-list-header';
import MenuListItem from './menu-list-item';
import Search from '../search';
import Poster from '../poster';

export type MenuListItemProps = {
  readonly id: string;
  readonly primaryText: string;
  readonly secondaryText: string;
  readonly caption: string;
  readonly isDisabled?: boolean;
  readonly isRestrictive?: boolean;
};

export interface Props {
  readonly items: MenuListItemProps[];
  readonly checkedItems: MenuListItemProps[];
  readonly isLoading: boolean;
  readonly isSearching?: boolean;
  readonly isFetching?: boolean;
  readonly search?: string;
  readonly onSearch: (filter: string) => void;
  readonly onToggleSelect: (item: MenuListItemProps) => void;
  readonly onToggleSelectAll: () => void;
  readonly selectionLimit?: number;
  readonly searchPlaceholder?: string;
  readonly errorMessage: string;
  readonly poster?: PosterProps;
  readonly maxItems?:number;
  readonly onAllAdded?:() => void;
  readonly onSelectAllExisting:() => void;
  readonly allExistingCount:number;
  readonly pageLimit:number;
  readonly unselectAllExisting:()=> void;
  readonly hasAllExisting:boolean;
  readonly sortOrder?: 'asc' | 'desc';
  readonly onSort?: () => void;
}

export interface PosterProps {
  Image: React.FC;
  imageWidth?: string;
  imageHeight?: string;
  heading?: string;
  description?: string;
}

const StyledList = styled(List)({
  background: 'transparent',
  height: '100%',
});

function MenuList({
  poster, items, checkedItems, ...props
}: Props) {
  const hasAllChecked = useMemo(
    () => checkedItems.length === items.length,
    [checkedItems, items],
  );

  const selectionLimitIsReached = props.selectionLimit != null
    && checkedItems.length >= props.selectionLimit;

  const rowContent = (index: number, item: MenuListItemProps) => {
    const isChecked = props.hasAllExisting || checkedItems.some((i) => i.id === item.id);
    const isDisabled = props.selectionLimit != null
      && selectionLimitIsReached
      && !isChecked;

    return (
      <MenuListItem
        item={item}
        labelID={`list-label-${item.id}`}
        divider={!!items[index + 1]}
        isChecked={isChecked}
        isDisabled={isDisabled}
        onToggle={props.onToggleSelect}
      />
    );
  };

  return (
    <StyledList disablePadding>
      <DialogTitle>
        <Search
          setGlobalFilter={props.onSearch}
          placeholder={props.searchPlaceholder}
          disabled={poster && !props.isSearching}
        />
      </DialogTitle>
      <Divider />
      {!!items.length && !poster && !props.isLoading && (
        <MenuListHeader
          length={checkedItems.length}
          onSelectAll={props.onToggleSelectAll}
          isSelectAll={hasAllChecked}
          selectAllExisting={props.onSelectAllExisting}
          allExistingCount={props.allExistingCount}
          pageLimit={props.pageLimit}
          unselectAllExisting={props.unselectAllExisting}
          hasAllExisting={props.hasAllExisting}
          isSearching={props.isSearching}
          hideSelectAll={props.selectionLimit != null}
          selectionLimit={props.selectionLimit}
          sortOrder={props.sortOrder}
          onSort={props.onSort}
        />
      )}
      <Box height={poster || props.isLoading ? 450 : 400} overflow="hidden">
        {props.isFetching && <LoadingState isLoading={props.isLoading} />}
        {poster && !props.errorMessage && !props.isFetching && (
          <Stack alignItems="center" justifyContent="center" style={{ height: '450px' }} flexDirection="column">
            <Poster
              Image={poster.Image}
              imageWidth={poster.imageWidth}
              imageHeight={poster.imageHeight}
              heading={poster.heading}
              description={poster.description}
            />
          </Stack>
        )}
        {!!props.errorMessage && props.errorMessage}
        {!poster && !props.errorMessage && !props.isLoading && (
          <Virtuoso
            data={items ?? []}
            totalCount={items?.length}
            itemContent={rowContent}
            atBottomThreshold={10}
            style={{ height: 400 }}
            overscan={500}
          />
        )}
      </Box>
    </StyledList>
  );
}

type LoadingStateProps = { isLoading: boolean };
const LoadingState = ({ isLoading }: LoadingStateProps) => (
  <Box display="flex" height={isLoading ? 450 : 400} justifyContent="center" alignItems="center">
    <CircularProgress />
  </Box>
);

export default React.memo(MenuList);
